import { IRRInputs } from '@/types/irr';

export interface TreasuryVsLeaseResult {
  savingOnTreasury: number;
  savingOnTreasuryPercent: number;
}

export function calculateTreasuryVsLeaseSaving(inputs: IRRInputs): TreasuryVsLeaseResult {
  const assetCost = inputs.totalAssetCost;
  const baseCost = assetCost / (1 + inputs.gstRate / 100);
  const gstAmount = assetCost - baseCost;
  const securityDepositPercent = inputs.securityDepositPercent / 100;
  const taxRate = inputs.corporateTaxBenefit / 100;
  const roiTreasury = (inputs.roiOnLoan ?? 7.5) / 100;
  const roiAfterTax = roiTreasury * (1 - taxRate);
  const tenureYears = inputs.tenureMonths / 12;
  const depRate = (inputs.depreciationPercent ?? 15) / 100;
  const pfPercent = inputs.processingFeesPercent / 100;
  const depositRefundPercent = inputs.refundSecurityDepositPercent / 100;
  const isQuarterly = inputs.paymentFrequency === 'quarterly';

  const gstPassbackEnabled = inputs.gstPassbackEnabled;
  const ptpmPostGstPassback = gstPassbackEnabled
    ? inputs.ptpmConsideration - inputs.gstPassback
    : inputs.ptpmConsideration;
  const finalPtpm = ptpmPostGstPassback;

  const netUpfrontOutflow = assetCost - (assetCost * securityDepositPercent);

  // Treasury side
  const treasuryLoss = -netUpfrontOutflow * Math.pow(1 + roiAfterTax, tenureYears) + netUpfrontOutflow;
  const treasuryInitialPayment = -assetCost;
  const treasuryGstItc = gstAmount;

  // Depreciation
  let totalDepreciation = 0;
  let openingValue = baseCost;
  for (let y = 0; y < Math.ceil(inputs.tenureMonths / 12); y++) {
    const dep = openingValue * depRate;
    totalDepreciation += dep;
    openingValue -= dep;
  }
  const treasuryTaxSaving = totalDepreciation * taxRate;
  const treasuryItClaim = treasuryTaxSaving;

  const treasuryNetCost = -(treasuryLoss + treasuryInitialPayment + treasuryGstItc + treasuryItClaim);

  // Lease side
  const nPayments = isQuarterly ? inputs.tenureMonths / 3 : inputs.tenureMonths;
  const periodicRental = assetCost * finalPtpm / 1000;
  const totalRentals = -periodicRental * nPayments;
  const leasePf = -assetCost * pfPercent;
  const leaseInitialPayment = -assetCost * securityDepositPercent;
  const leaseRefund = assetCost * depositRefundPercent;
  const leaseItClaim = -totalRentals * taxRate;

  let leaseAdvanceRental = 0;
  if (inputs.advanceRentalPeriods > 0) {
    leaseAdvanceRental = -periodicRental * inputs.advanceRentalPeriods;
  }

  const leaseNetCost = -(totalRentals + leaseAdvanceRental + leasePf + leaseInitialPayment + leaseRefund + leaseItClaim);
  const savingOnTreasury = treasuryNetCost - leaseNetCost;
  const savingOnTreasuryPercent = savingOnTreasury / assetCost;

  return { savingOnTreasury, savingOnTreasuryPercent };
}
